import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  FormGroup,
  Label,
  Alert,
  Spinner,
} from "reactstrap";
import { getPartByLabel, addImpactedPart, getTicketConstants } from "./service";

// Inline styles for modal backdrop to ensure it's above the Offcanvas
const modalBackdropStyle = document.createElement("style");
modalBackdropStyle.innerHTML = `
  .modal-backdrop-high {
    z-index: 1055 !important;
  }
  .modal-high {
    z-index: 1060 !important;
  }
`;
if (typeof document !== "undefined" && !document.getElementById("modal-high-z-index")) {
  modalBackdropStyle.id = "modal-high-z-index";
  document.head.appendChild(modalBackdropStyle);
}

const AddPartsModal = ({ isOpen, toggle, ticketId, orderId, onPartAdded }) => {
  const [step, setStep] = useState("search"); // "search" | "configure"
  const [searchLabel, setSearchLabel] = useState("");
  const [searchedPart, setSearchedPart] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [constants, setConstants] = useState(null);

  // Part configuration
  const [partDetails, setPartDetails] = useState({
    issue_description: "",
    categories: [],
    image: null,
  });

  useEffect(() => {
    if (isOpen) {
      loadConstants();
      resetModal();
    }
  }, [isOpen]);

  const loadConstants = async () => {
    try {
      const data = await getTicketConstants();
      setConstants(data);
    } catch (err) {
      console.error("Failed to load constants", err);
    }
  };

  const resetModal = () => {
    setStep("search");
    setSearchLabel("");
    setSearchedPart(null);
    setSearchError(null);
    setPartDetails({
      issue_description: "",
      categories: [],
      image: null,
    });
  };

  const handleSearch = async () => {
    if (!searchLabel.trim()) {
      setSearchError("Please enter a part label");
      return;
    }

    try {
      setSearching(true);
      setSearchError(null);
      const data = await getPartByLabel(orderId, searchLabel.trim());

      // Validate response structure (axios interceptor returns response.data directly)
      if (!data || !data.part) {
        console.error("Invalid response structure:", data);
        setSearchError("Invalid response from server");
        setSearchedPart(null);
        return;
      }

      setSearchedPart(data.part);
      setStep("configure");
    } catch (err) {
      // Axios interceptor transforms errors - check for error message directly
      // For 404 or validation errors, show the error message
      if (err.error) {
        if (typeof err.error === 'string') {
          setSearchError(err.error);
        } else if (err.error?.base?.[0]) {
          setSearchError(err.error.base[0]);
        } else if (err.error?.label?.[0]) {
          setSearchError(err.error.label[0]);
        } else {
          setSearchError("Part not found in this order");
        }
      } else {
        // Network error or other issue - don't show to user
        console.error("Error searching for part:", err);
      }
      setSearchedPart(null);
    } finally {
      setSearching(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && step === "search") {
      handleSearch();
    }
  };

  const handleCategoryToggle = (category) => {
    const categories = partDetails.categories || [];
    if (categories.includes(category)) {
      setPartDetails({
        ...partDetails,
        categories: categories.filter((c) => c !== category),
      });
    } else {
      setPartDetails({
        ...partDetails,
        categories: [...categories, category],
      });
    }
  };

  const validatePartDetails = () => {
    if (!partDetails.issue_description.trim()) {
      return "Issue description is required";
    }
    if (!partDetails.categories || partDetails.categories.length === 0) {
      return "At least one category is required";
    }
    return null;
  };

  const handleSubmit = async () => {
    const error = validatePartDetails();
    if (error) {
      alert(error);
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append("order_part_id", searchedPart.id);
      formData.append("issue_description", partDetails.issue_description);
      formData.append("categories", JSON.stringify(partDetails.categories));
      if (partDetails.image) {
        formData.append("issue_image", partDetails.image);
      }

      await addImpactedPart(ticketId, formData);

      if (onPartAdded) onPartAdded();
      toggle();
      resetModal();
    } catch (err) {
      // Axios interceptor transforms errors
      const errorMessage = err.error?.base?.[0] || err.error || err.message || "Failed to add part";
      alert("Failed to add part: " + errorMessage);
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    setStep("search");
    setSearchedPart(null);
    setSearchError(null);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      backdrop="static"
      zIndex={1060}
      backdropClassName="modal-backdrop-high"
      modalClassName="modal-high"
    >
      <ModalHeader toggle={toggle}>
        <h5 className="mb-0 fw-bold">
          {step === "search" ? "Add Part to Ticket" : "Configure Part Details"}
        </h5>
      </ModalHeader>
      <ModalBody>
        {step === "search" && (
          <>
            <FormGroup>
              <Label className="fw-bold">Search by Panel Label</Label>
              <div className="d-flex gap-2">
                <Input
                  type="text"
                  placeholder="Enter panel label (e.g., 25 C/2)"
                  value={searchLabel}
                  onChange={(e) => {
                    setSearchLabel(e.target.value);
                    setSearchError(null);
                  }}
                  onKeyPress={handleKeyPress}
                  disabled={searching}
                  style={{ pointerEvents: "auto", cursor: "text", position: "relative", zIndex: 1 }}
                />
                <Button
                  color="primary"
                  onClick={handleSearch}
                  disabled={searching || !searchLabel.trim()}
                  style={{ minWidth: "120px" }}
                >
                  {searching ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-search me-2"></i>Search
                    </>
                  )}
                </Button>
              </div>
            </FormGroup>

            {searchError && (
              <Alert color="danger" className="mb-0">
                <i className="fas fa-exclamation-circle me-2"></i>
                {searchError}
              </Alert>
            )}
          </>
        )}

        {step === "configure" && searchedPart && (
          <>
            {/* Part Information */}
            <div className="border rounded p-2 mb-3 bg-light">
              <div className="row">
                <div className="col-md-6">
                  <p className="mb-2">
                    <strong>Label:</strong> {searchedPart.label || "-"}
                  </p>
                  <p className="mb-2">
                    <strong>Name:</strong> {searchedPart.name || searchedPart.content?.name || "-"}
                  </p>
                </div>
                <div className="col-md-6">
                  {(searchedPart.content?.length_2 || searchedPart.content?.length_1 || searchedPart.content?.thickness) && (
                    <p className="mb-2">
                      <strong>Dimensions:</strong>{" "}
                      {searchedPart.content.length_1} × {searchedPart.content.length_2}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <FormGroup>
              <Label className="fw-bold">
                Issue Category <span className="text-danger">*</span>
              </Label>
              <div className="d-flex flex-wrap gap-2">
                {constants?.issue_categories?.map((cat) => (
                  <Button
                    key={cat}
                    color={partDetails.categories?.includes(cat) ? "primary" : "outline-secondary"}
                    size="sm"
                    onClick={() => handleCategoryToggle(cat)}
                  >
                    {cat}
                  </Button>
                ))}
              </div>
              <small className="text-muted">Select all categories that apply</small>
            </FormGroup>

            {/* Issue Details Form */}
            <FormGroup>
              <Label className="fw-bold">
                Issue Description <span className="text-danger">*</span>
              </Label>
              <Input
                type="textarea"
                rows={4}
                value={partDetails.issue_description}
                onChange={(e) =>
                  setPartDetails({
                    ...partDetails,
                    issue_description: e.target.value,
                  })
                }
                placeholder="Describe the issue with this part..."
              />
            </FormGroup>

            <FormGroup>
              <Label className="fw-bold">
                Issue Image
              </Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  setPartDetails({
                    ...partDetails,
                    image: e.target.files[0],
                  })
                }
              />
              <small className="text-muted">
                Optional - Upload a photo showing the issue
              </small>
              {partDetails.image && (
                <div className="mt-2">
                  <i className="fas fa-check-circle text-success me-2"></i>
                  <span className="text-success">{partDetails.image.name}</span>
                </div>
              )}
            </FormGroup>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {step === "search" && (
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
        )}
        {step === "configure" && (
          <>
            <Button color="secondary" onClick={handleBack}>
              <i className="fas fa-arrow-left me-2"></i>Back
            </Button>
            <Button color="primary" onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Adding Part...
                </>
              ) : (
                <>
                  <i className="fas fa-plus me-2"></i>Add Part to Ticket
                </>
              )}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default AddPartsModal;
